import { monthNumberMapping } from "utils/date";

const MONTH_MIN = 1;
const MONTH_MAX = 12;

export const MONTHS = Array.from({ length: MONTH_MAX - MONTH_MIN + 1 }, (_, index) => MONTH_MIN + index);

const MONTH_SHORT_NAMES = Object.values(monthNumberMapping);

type MonthRange = {
  end: number;
  start: number;
};

const BRACKET_PATTERN = /^\[(.*)\]$/;

const isValidMonth = (month: number) => Number.isInteger(month) && month >= MONTH_MIN && month <= MONTH_MAX;

const isValidMonthRange = (range: MonthRange) =>
  isValidMonth(range.start) && isValidMonth(range.end) && range.start <= range.end;

export const formatMonthOptionLabel = (month: number) => `${month}-${MONTH_SHORT_NAMES[month - 1]}`;

export const MONTH_OPTION_LABELS = MONTHS.map(formatMonthOptionLabel);

const formatRangeToken = (range: MonthRange) =>
  range.start === range.end ? `${range.start}` : `${range.start}-${range.end}`;

const parseRangeToken = (segment: string): MonthRange | null => {
  const trimmed = segment.trim();
  if (!trimmed) return null;

  if (!trimmed.includes("-")) {
    const month = Number(trimmed);
    return isValidMonth(month) ? { end: month, start: month } : null;
  }

  const [startRaw, endRaw] = trimmed.split("-");
  const start = Number(startRaw);
  const end = Number(endRaw);

  return isValidMonthRange({ end, start }) ? { end, start } : null;
};

const expandRangeToMonths = (range: MonthRange): number[] =>
  Array.from({ length: range.end - range.start + 1 }, (_, index) => range.start + index);

const compressMonthsToRanges = (months: number[]): MonthRange[] => {
  const sorted = [...new Set(months.filter(isValidMonth))].sort((a, b) => a - b);
  if (!sorted.length) return [];

  const ranges: MonthRange[] = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let index = 1; index < sorted.length; index += 1) {
    const month = sorted[index];
    if (month === end + 1) {
      end = month;
      continue;
    }

    ranges.push({ end, start });
    start = month;
    end = month;
  }

  ranges.push({ end, start });
  return ranges;
};

const parseMonthRanges = (value: null | string | undefined): MonthRange[] => {
  if (!value?.trim()) return [];

  const trimmed = value.trim();
  const inner = BRACKET_PATTERN.test(trimmed) ? trimmed.slice(1, -1).trim() : trimmed;
  if (!inner) return [];

  const ranges: MonthRange[] = [];

  for (const token of inner.split(",")) {
    const parsed = parseRangeToken(token);
    if (parsed) ranges.push(parsed);
  }

  return ranges;
};

export const rangeTextToSelectedMonths = (value: null | string | undefined): number[] => {
  const months = new Set<number>();
  parseMonthRanges(value).forEach((range) => expandRangeToMonths(range).forEach((month) => months.add(month)));
  return [...months].sort((a, b) => a - b);
};

export const selectedMonthsToRangeText = (months: number[]): string => {
  const ranges = compressMonthsToRanges(months);
  if (!ranges.length) return "";
  return ranges.map(formatRangeToken).join(",");
};

const serializeMonthRanges = (ranges: MonthRange[]): string => {
  if (!ranges.length) return "[]";
  return `[${ranges.map(formatRangeToken).join(",")}]`;
};

const formatMonthRanges = (ranges: MonthRange[]): string => {
  if (!ranges.length) return "";
  return ranges.map(formatRangeToken).join(", ");
};

export const formatMonthRangeText = (value: null | string | undefined): string => {
  if (!value?.trim()) return "";
  return formatMonthRanges(parseMonthRanges(value));
};

export const fromApiMonthRanges = (value: null | string | undefined): string => formatMonthRangeText(value);

export const toApiMonthRanges = (value: string): string | undefined => {
  const rangeText = selectedMonthsToRangeText(rangeTextToSelectedMonths(value));
  if (!rangeText) return undefined;
  return serializeMonthRanges(parseMonthRanges(rangeText));
};
